/**
 * Sentry observability layer — MySkool (shared across school-web + superadmin-web).
 *
 * Env-gated Sentry SDK wrapper for error monitoring, performance tracing, and session replay.
 * Mirrors the Clarity layer's API surface so consumers can instrument both sinks similarly.
 *
 * Design rules (identical posture to clarity.ts):
 *  - NEVER throws or blocks UI. Every public fn is a no-op when disabled.
 *  - Disabled by default outside production unless VITE_ENABLE_SENTRY=true.
 *  - Never sends PII: values sanitized, endpoints normalized, durations bucketed.
 */

// Dynamic import — @sentry/react is NOT loaded until initSentry() is called,
// keeping the critical path small (GC-D15 bundle budget).
import type * as Sentry from '@sentry/react';

let _sentry: typeof Sentry | null = null;

function S(): typeof Sentry | null {
  return _sentry;
}

// ── Environment (Vite inlines at build time) ────────────────────────────────
const ENV = {
  dsn: import.meta.env.VITE_SENTRY_DSN as string | undefined,
  enableFlag: import.meta.env.VITE_ENABLE_SENTRY as string | undefined,
  environment: (import.meta.env.VITE_APP_ENV as string) || import.meta.env.MODE || 'development',
  appVersion: (import.meta.env.VITE_APP_VERSION as string) || 'unknown',
  releaseSha: (import.meta.env.VITE_RELEASE_SHA as string) || 'unknown',
  mode: import.meta.env.MODE,
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.2),
  replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0.1),
  replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 1.0),
  enableLogs: import.meta.env.VITE_SENTRY_ENABLE_LOGS === 'true',
  appName: import.meta.env.VITE_APP_NAME ?? 'myskool',
};

const TEST_FLAG = '__myskool_sentry_force__';
let cachedEnabled: boolean | undefined;

export function isSentryEnabled(): boolean {
  if (cachedEnabled !== undefined) return cachedEnabled;
  cachedEnabled = computeEnabled();
  return cachedEnabled;
}

export function __resetSentryEnabled(): void {
  cachedEnabled = undefined;
}

function computeEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (!ENV.dsn) return false;
  if (ENV.enableFlag === 'false') return false;
  if (ENV.mode === 'production') return true;
  if (ENV.enableFlag === 'true') return true;
  try { return window.localStorage.getItem(TEST_FLAG) === '1'; } catch { return false; }
}

// ── Helpers ──────────────────────────────────────────────────────────────────
export function normalizeEndpoint(url: string): string {
  try {
    const u = new URL(url, 'http://x');
    return u.pathname
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:uuid')
      .replace(/\/\d+/g, '/:id');
  } catch {
    return url
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/:uuid')
      .replace(/\/\d+/g, '/:id');
  }
}

export type DurationBucket = 'lt_1s' | '1_3s' | '3_10s' | '10_30s' | '30_60s' | 'gt_60s';

export function bucketDuration(ms: number): DurationBucket {
  if (ms < 1000) return 'lt_1s';
  if (ms < 3000) return '1_3s';
  if (ms < 10000) return '3_10s';
  if (ms < 30000) return '10_30s';
  if (ms < 60000) return '30_60s';
  return 'gt_60s';
}

function sanitize(val: unknown, max = 100): string | undefined {
  if (val === null || val === undefined) return undefined;
  const s = String(val).trim();
  if (!s) return undefined;
  return s.length > max ? s.slice(0, max) : s;
}

// ── Init ─────────────────────────────────────────────────────────────────────

export function initSentry(): void {
  if (_sentry || !isSentryEnabled()) return;

  // Kick off async dynamic import immediately; callers never await.
  import('@sentry/react').then((mod) => {
    _sentry = mod;
    try {
      mod.init({
        dsn: ENV.dsn,
        environment: ENV.environment,
        release: ENV.appVersion,

        tracesSampleRate: ENV.tracesSampleRate,
        tracePropagationTargets: ['localhost', /^https:\/\/[^/]+\.ingest\.us\.sentry\.io/],

        integrations: [
          mod.browserTracingIntegration(),
          mod.replayIntegration({ maskAllText: true, blockAllMedia: true }),
        ],
        replaysSessionSampleRate: ENV.replaysSessionSampleRate,
        replaysOnErrorSampleRate: ENV.replaysOnErrorSampleRate,
        enableLogs: ENV.enableLogs,

        dataCollection: {
          userInfo: false,
          httpBodies: [],
          queryParams: false,
        },
      });

      mod.setTag('app_name', ENV.appName);
      mod.setTag('app_env', ENV.environment);
      mod.setTag('release_sha', ENV.releaseSha);

      installGlobalListeners();
    } catch {
      /* Sentry must never break the app */
    }
  }).catch(() => {
    /* dynamic import failure — Sentry unavailable, noop */
  });
}

function installGlobalListeners(): void {
  if (typeof window === 'undefined') return;
  const sentry = S();
  if (!sentry) return;
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    try {
      if (e.reason instanceof Error) {
        sentry.captureException(e.reason, { tags: { source: 'unhandledrejection' } });
      } else {
        sentry.captureMessage(`Unhandled rejection: ${String(e.reason)}`, {
          level: 'error',
          tags: { source: 'unhandledrejection' },
        });
      }
    } catch { /* noop */ }
  });
}

// ── User identity ────────────────────────────────────────────────────────────
export function identifySentryUser(ctx: {
  userId?: string;
  role?: string;
  plan?: string;
  authState?: string;
  onboardingState?: string;
} | null): void {
  const sentry = S();
  if (!sentry || !isSentryEnabled()) return;
  try {
    if (ctx) {
      sentry.setUser({
        id: sanitize(ctx.userId) ?? '',
        role: sanitize(ctx.role) ?? undefined,
        plan: sanitize(ctx.plan) ?? undefined,
        auth_state: ctx.authState,
        onboarding_state: ctx.onboardingState,
      });
    } else {
      sentry.setUser(null);
    }
  } catch { /* noop */ }
}

// ── Tags ─────────────────────────────────────────────────────────────────────
export function setSentryTags(tags: Record<string, string | undefined>): void {
  const sentry = S();
  if (!sentry || !isSentryEnabled()) return;
  try {
    for (const [key, value] of Object.entries(tags)) {
      const safe = sanitize(value);
      if (safe !== undefined) sentry.setTag(key, safe);
    }
  } catch { /* noop */ }
}

// ── Route ─────────────────────────────────────────────────────────────────────
export function trackSentryRoute(ctx: { path: string; pageType?: string }): void {
  const sentry = S();
  if (!sentry || !isSentryEnabled()) return;
  try {
    const norm = normalizeEndpoint(ctx.path);
    sentry.setTag('route_id', norm);
    sentry.setTag('route_group', norm.split('/')[1] || '/');
    if (ctx.pageType) sentry.setTag('page_type', ctx.pageType);
  } catch { /* noop */ }
}

// ── Error capture ────────────────────────────────────────────────────────────
export function captureSentryException(
  error: Error | string,
  context?: { area?: string; level?: 'fatal' | 'error' | 'warning' | 'info'; tags?: Record<string, string> }
): void {
  const sentry = S();
  if (!sentry || !isSentryEnabled()) return;
  try {
    sentry.withScope((scope) => {
      if (context?.area) scope.setTag('error_area', context.area);
      if (context?.level) scope.setLevel(context.level);
      if (context?.tags) Object.entries(context.tags).forEach(([k, v]) => scope.setTag(k, v));
      if (typeof error === 'string') sentry.captureMessage(error, context?.level ?? 'error');
      else sentry.captureException(error);
    });
  } catch { /* noop */ }
}

// ── API failure ──────────────────────────────────────────────────────────────
export function captureSentryApiFailure(ctx: {
  method: string;
  endpoint: string;
  statusCode?: number;
  statusClass?: string;
  errorCode?: string;
}): void {
  const sentry = S();
  if (!sentry || !isSentryEnabled()) return;
  try {
    sentry.addBreadcrumb({
      category: 'api',
      message: `${ctx.method} ${ctx.endpoint}`,
      level: ctx.statusCode && ctx.statusCode >= 500 ? 'error' : 'warning',
      data: { method: ctx.method, endpoint: ctx.endpoint, status: ctx.statusCode },
    });
    if (ctx.statusCode && ctx.statusCode >= 500) {
      const err = new Error(`API ${ctx.statusCode} on ${ctx.endpoint}`);
      err.name = 'ApiError';
      captureSentryException(err, { area: 'api', level: 'error', tags: { http_status: String(ctx.statusCode) } });
    }
  } catch { /* noop */ }
}

// ── Performance ──────────────────────────────────────────────────────────────
export function captureSentryPerformance(signal: { name: string; bucket: DurationBucket }): void {
  const sentry = S();
  if (!sentry || !isSentryEnabled()) return;
  try {
    if (signal.bucket === 'lt_1s' || signal.bucket === '1_3s') return;
    sentry.addBreadcrumb({
      category: 'performance',
      message: `${signal.name} ${signal.bucket}`,
      level: 'warning',
      data: { name: signal.name, bucket: signal.bucket },
    });
  } catch { /* noop */ }
}

// ── Breadcrumb ───────────────────────────────────────────────────────────────
export function addSentryBreadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
  const sentry = S();
  if (!sentry || !isSentryEnabled()) return;
  try {
    sentry.addBreadcrumb({ category, message, level: 'info', data });
  } catch { /* noop */ }
}
